import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { Menu, X, LogOut, User } from 'lucide-react';
import logo from '../assets/images/logo-navbar.png';
import LanguagePicker from '../components/LanguagePicker';
import { getRole, logout, AUTH_CHANGED_EVENT } from '../utils/authUtils';
import { isStaffRole } from '../utils/staffRoles';

const HeaderWrapper = styled.header`
  background: transparent;
  padding-left: 2rem;
  padding-right: 4rem;
  padding-top: 0rem;
  padding-bottom: 0rem;
  @media (max-width: 768px) {
    padding-left: 1rem;
    padding-right: 1rem;
  }
`;

const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled(Link)`
  height: 75px;
  margin-left: 10px;

  @media (max-width: 768px) {
    height: 60px;
    margin-left: 0;
  }
`;

const LogoImage = styled.img`
  height: 100%;
  object-fit: contain;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileMenu = styled.div`
  display: none;
  position: absolute;
  top: 90px;
  left: 0;
  right: 0;
  background: #90C4D9;
  flex-direction: column;
  align-items: center;
  padding: 1rem;
  z-index: 10000;
  gap: 1rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  &.open {
    display: flex;
  }
`;

const Divider = styled.div`
  width: 90%;
  border-top: 1px solid white;
  margin: 0.5rem 0;
`;

const NavItem = styled.span`
  color: white;
  text-decoration: none;
  font-weight: bold;
  font-size: 1.2rem;
  position: relative;

  a {
    color: white;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  &::after {
    content: '.';
    color: #47ABDB;
    margin-left: 2px;
  }
`;

const LanguagePickerWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-left: 2rem;

  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const Separator = styled.span`
  color: white;
  font-weight: bold;
  font-size: 1.2rem;
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: white;
  cursor: pointer;

  @media (max-width: 768px) {
    display: block;
    margin-left: 1rem;
  }
`;

// Auth controls live in the shared navbar now, not inside individual portal
// pages (the former Speaker Hub page used to render its own absolutely-positioned
// Logout button - removed in favor of this single, always-visible spot).
// Styled to match heartport-public-frontend's Header: a plain "Sign in" nav
// item when logged out, and a user-icon dropdown ("Profile" / "Sign out")
// when logged in.
const AuthSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-left: 1.5rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavItemLink = styled(Link)`
  color: white;
  text-decoration: none;
  font-weight: bold;
  font-size: 1.2rem;
  position: relative;

  &:hover {
    text-decoration: underline;
  }

  &::after {
    content: '.';
    color: #47ABDB;
    margin-left: 2px;
  }
`;

const ProfileMenuWrapper = styled.div`
  position: relative;
`;

const ProfileTrigger = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 0;

  &:hover {
    color: #47ABDB;
  }
`;

const ProfileDropdown = styled.div`
  position: ${(props) => (props.$mobile ? 'static' : 'absolute')};
  right: 0;
  margin-top: 0.5rem;
  width: 12rem;
  background: white;
  border-radius: 6px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  padding: 0.25rem 0;
  z-index: 50;
`;

const ProfileDropdownLink = styled(Link)`
  display: block;
  padding: 0.5rem 1rem;
  color: #374151;
  text-decoration: none;
  font-size: 0.95rem;

  &:hover {
    background: #f3f4f6;
  }
`;

const ProfileDropdownButton = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.5rem 1rem;
  background: none;
  border: none;
  color: #dc2626;
  font-size: 0.95rem;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: #f3f4f6;
  }
`;

// The "Profile" menu link's destination. Every non-admin role goes to /profile - it shows the
// person's own data, their invitations (answered there or on the respond page) and, for a speaker,
// the speaker area, so it is the universal account surface rather than a role-specific landing spot.
// Staff (ADMIN or TEAM) keep the admin dashboard as "home"; that's a distinct concept from a personal
// profile. The primary role is MEMBER, TEAM or ADMIN (V65, V70; SPEAKER is computed, never stored).
const roleHome = (role) => {
  if (isStaffRole(role)) return '/admin/dashboard';
  return '/profile';
};

const Header = () => {
  const intl = useIntl();
  const t = (id, defaultMessage) => intl.formatMessage({ id, defaultMessage });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [role, setRole] = useState(() => getRole());
  const location = useLocation();
  const navigate = useNavigate();

  // localStorage isn't reactive, so re-read the role whenever the route
  // changes - covers both a fresh login elsewhere and a logout redirect.
  useEffect(() => {
    setRole(getRole());
  }, [location.pathname]);

  // Route changes alone miss cases like completing signup, which stores a
  // session without ever navigating away from /signup - listen for the
  // explicit signal instead (see AUTH_CHANGED_EVENT in authUtils.js).
  useEffect(() => {
    const handleAuthChanged = () => setRole(getRole());
    window.addEventListener(AUTH_CHANGED_EVENT, handleAuthChanged);
    return () => window.removeEventListener(AUTH_CHANGED_EVENT, handleAuthChanged);
  }, []);

  const handleLogout = () => {
    logout();
    setRole(null);
    setIsMenuOpen(false);
    navigate('/');
  };

  const ExternalLink = ({ to, children }) => {
    return (
      <NavItem>
        <a href={to} target="_blank" rel="noopener noreferrer">{children}</a>
      </NavItem>
    );
  };

  const AuthControls = ({ mobile }) => {
    const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);

    if (!role) {
      return (
        <NavItemLink to="/login" onClick={() => setIsMenuOpen(false)}>
          {t('header.login', 'Sign in')}
        </NavItemLink>
      );
    }

    return (
      <ProfileMenuWrapper>
        <ProfileTrigger onClick={() => setIsProfileMenuOpen((open) => !open)}>
          <User size={20} />
        </ProfileTrigger>
        {isProfileMenuOpen && (
          <ProfileDropdown $mobile={mobile}>
            <ProfileDropdownLink
              to={roleHome(role)}
              onClick={() => { setIsProfileMenuOpen(false); setIsMenuOpen(false); }}
            >
              {t('header.myProfile', 'Profile')}
            </ProfileDropdownLink>
            {/* Straight into the edit form instead of the read-only profile:
                the only edit affordance used to be a pencil badge over the
                photo on /profile, which most account holders never found.
                /profile?edit=1 opens the profile page with the edit modal
                already up (AttendeeProfile consumes the flag). Always
                /profile, including for ADMIN - the profile page is the
                universal account surface, while roleHome(ADMIN) is a
                dashboard, not a profile. */}
            <ProfileDropdownLink
              to="/profile?edit=1"
              onClick={() => { setIsProfileMenuOpen(false); setIsMenuOpen(false); }}
            >
              {t('header.editProfile', 'Edit my profile')}
            </ProfileDropdownLink>
            <ProfileDropdownButton
              onClick={() => { handleLogout(); setIsProfileMenuOpen(false); }}
            >
              <LogOut size={16} style={{ marginRight: '0.5rem' }} />
              {t('header.logout', 'Sign out')}
            </ProfileDropdownButton>
          </ProfileDropdown>
        )}
      </ProfileMenuWrapper>
    );
  };

  return (
    <HeaderWrapper>
      <Nav>
        <Logo to="/">
          <LogoImage src={logo} alt="HEARTS Logo" />
        </Logo>

        <RightSection>
          <NavLinks>
            <ExternalLink to="https://public.heartport.org">HeartPort Public</ExternalLink>
            <ExternalLink to="https://expert.heartport.org">HeartPort Expert</ExternalLink>
            <ExternalLink to="https://public.heartport.org/crew">Crew</ExternalLink>
          </NavLinks>

          <LanguagePickerWrapper>
            <Separator>|</Separator>
            <LanguagePicker />
            <Separator>|</Separator>
          </LanguagePickerWrapper>

          <AuthSection>
            <AuthControls />
          </AuthSection>

          <MobileMenuButton onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </MobileMenuButton>
        </RightSection>
      </Nav>

      <MobileMenu className={isMenuOpen ? 'open' : ''}>
        <ExternalLink to="https://public.heartport.org">HeartPort Public</ExternalLink>
        <ExternalLink to="https://expert.heartport.org">HeartPort Expert</ExternalLink>
        <ExternalLink to="https://public.heartport.org/crew">Crew</ExternalLink>
        <Divider />
        <AuthControls mobile />
      </MobileMenu>
    </HeaderWrapper>
  );
};

export default Header;
